import React from 'react';
import { withRouter } from 'react-router-dom';
import {
    Col,
    Row,
    FormGroup,
    Container,
    Button,
    List,
    Spinner,
} from 'reactstrap';
import { debounce } from 'lodash-es';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import { util } from '../Util';
import {
    AppPageForm, FormLabel, onFieldChange, toasty,
} from '../common/forms/FormElements';
import CustomCircularProgress from '../common/CustomCircularProgress';
import { ExecutionHistory } from './common/ExecutionHistory';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import AsyncSelect from 'react-select/async';
import { YesNoButtons } from '../common/YesNoButtons';

const Ordering = {
    FIRST: 'First',
    LAST: 'Last',
};

class AddChargeTypeToContractsViewModel {
    chargeTypeId = '';
    ordering = Ordering.FIRST;
    tenantId = null;
}

class AddChargeTypeToContracts extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.state = {
            formValidated: false,
            loading: false,
            saving: false,
            data: new AddChargeTypeToContractsViewModel(),
            isValidated: false,
            tenants: [],
            selectedChargeType: null,
            selectedTenant: null,
        };

        this.onChange = this.onChange.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }
    
    populateState = async () => {
        const [tenants] = await Promise.all([
            util.fetch.js(ApiRoutes.typeAheads.tenants()),
        ]);
    
        this.setState({
            tenants,
        });
    };

    onChange = onFieldChange;

    onSubmit = async () => {
        const { data } = this.state;

        this.setState({ saving: true });

        try {
            await util.fetch.post(
                ApiRoutes.adminTool.addChargeTypeToContracts(),
                data,
            );

            this.setState({
                data: new AddChargeTypeToContractsViewModel(),
                isValidated: false,
                selectedTenant: null,
                selectedChargeType: null,
            });

            toasty.success('The charge type has been added to all flagging contracts in the selected tenant.');

            // Refresh the table after successful submission
            await this.props.refreshUsageHistory();
        } catch (err) {
            toasty.error('Error adding charge type');
            await this.props.refreshUsageHistory();
        } finally {
            this.setState({ saving: false });
        }
    };

    onTenantChanged = (selectedOption) => {
        this.setState((prevState) => ({
            selectedTenant: selectedOption,
            selectedChargeType: null,
            data: {
                ...prevState.data,
                chargeTypeId: null,
                tenantId: selectedOption ? selectedOption.value : null,
            },
        }));
    };

    loadOptions = debounce((inputValue, callback) => {
        const { tenantId } = this.state.data;

        if (tenantId) {
            fetch(ApiRoutes.typeAheads.chargeTypeSearchByTenant(inputValue, tenantId))
                .then((response) => response.json())
                .then((data) => {
                    const options = data.map((item) => ({
                        label: item.label,
                        value: item.value,
                    }));
                    callback(options);
                });
        }
    }, 250);

    onChargeTypeChanged = async (selectedOption) => {
        this.setState((prevState) => ({
            selectedChargeType: selectedOption,
            data: {
                ...prevState.data,
                chargeTypeId: selectedOption ? selectedOption.value : null,
            },
        }));
    };

    render() {
        const {
            loading,
            saving,
            isValidated,
            tenants,
            selectedTenant,
        } = this.state;

        if (loading) {
            return (
                <div className="confirmationContainer h-100 pt-2 d-flex flex-row align-items-center justify-content-center">
                    <CustomCircularProgress
                        color="secondary"
                        variant="indeterminate"
                        label={
                            this.props.match.params.id
                                ? 'Getting Tool History...'
                                : 'Loading form...'
                        }
                    />
                </div>
            );
        }

        if (!(this.context ?? {}).user) return null;

        return (
            <AppPageForm
                backUrl={AppNavPaths.AdminTool}
                backLabel="Back to Admin Tools"
                formShown={this.context.formIsOpen}
                formId="adminToolList"
                formName="adminToolList"
                formHeading="Add Charge Type to Flagging Contracts"
                formRef={this.formRef}
                formIsColumn
                saving={saving}
                isValidated={isValidated}
                onSubmit={this.onSubmit}
                setIsValidated={(value) => {
                    this.setState({ isValidated: value });
                }}
            >
                <Container>
                    <Row>
                        <Col className="bg-light border pt-2" xs="12">
                            <div>
                                <FormGroup>
                                    <FormLabel htmlFor="selectedTenant" text="Select a tenant" required />
                                    <ValidatedSelect
                                        id="selectedTenant"
                                        name="data.selectedTenant"
                                        required
                                        options={tenants}
                                        value={selectedTenant}
                                        onChange={this.onTenantChanged}
                                        validationMessage="Select a tenant to add "
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <FormLabel htmlFor="chargeTypeId" text="Charge Type" required />
                                    <div className="d-flex flex-column">
                                        <AsyncSelect
                                            name="chargeTypeId"
                                            classNamePrefix="react-async"
                                            className="smallReactAsync"
                                            loadOptions={this.loadOptions}
                                            placeholder="Type Charge Type Here"
                                            id="chargeTypeId"
                                            value={this.state.selectedChargeType}
                                            onChange={this.onChargeTypeChanged}
                                            isClearable
                                            cacheOptions
                                            required
                                        />
                                    </div>
                                </FormGroup>

                                <Col xs={6} className="p-0">
                                    <YesNoButtons
                                        className="d-flex"
                                        label="Where should this appear within each contract's charge types?"
                                        name="ordering"
                                        value={this.state.data.ordering}
                                        required
                                        onClick={(value) => {
                                            const { data } = this.state;
                                            data.ordering = value;

                                            this.setState({ data });
                                        }}
                                        options={[
                                            { value: Ordering.FIRST, label: 'First' },
                                            { value: Ordering.LAST, label: 'Last' },
                                        ]}
                                    />
                                </Col>

                                <div className="d-flex justify-content-center">
                                    <Button
                                        className="btn mr-2 mt-3 mb-2"
                                        color="primary"
                                        type="submit"
                                        disabled={saving}
                                    >
                                        {!saving && <span>Submit</span>}
                                        {saving && (
                                            <>
                                                <Spinner
                                                    size="sm"
                                                    className="saving-button-progress text-success mr-2"
                                                />
                                                <span>
                                                    Submitting, please wait...
                                                </span>
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>

                <ExecutionHistory usageHistory={this.props.usageHistory}>
                    {(record) => (
                        <List type="unstyled">
                            {record.input.chargeType && (
                                <li>
                                    <strong>Charge Type:</strong>
                                    {' '}
                                    {record.input.chargeType}
                                </li>
                            )}

                            {record.input.tenantId && (
                                <li>
                                    <strong>Tenant ID:</strong>
                                    {' '}
                                    {record.input.tenantId}
                                </li>
                            )}

                            {record.input.tenantName && (
                                <li>
                                    <strong>Tenant:</strong>
                                    {' '}
                                    {record.input.tenantName}
                                </li>
                            )}

                            {record.input.ordering && (
                                <li>
                                    <strong>Order:</strong>
                                    {' '}
                                    {record.input.ordering}
                                </li>
                            )}

                            {record.output.contracts && (
                                <li>
                                    <strong>Contracts Affected:</strong>
                                    {' '}
                                    {record.output.contracts}
                                </li>
                            )}
                        </List>
                    )}
                </ExecutionHistory>
            </AppPageForm>
        );
    }
}

export default withRouter(AddChargeTypeToContracts);
